import { MessageSquare, PenTool, Code, Rocket } from 'lucide-react';
import { ScrollReveal } from '@/hooks/useScrollReveal';

const STEPS = [
  {
    icon: MessageSquare,
    number: '01',
    title: 'Discovery',
    description: 'We start with a conversation. I learn about your business, goals, and what you need from your website.',
  },
  {
    icon: PenTool,
    number: '02',
    title: 'Design',
    description: 'I create a custom design based on your brand. You review it, we refine it together until it feels right.',
  },
  {
    icon: Code,
    number: '03',
    title: 'Build',
    description: 'Your approved design is built into a fully functional, fast, responsive website with clean code.',
  },
  {
    icon: Rocket,
    number: '04',
    title: 'Launch',
    description: 'Your website goes live. I handle the setup and make sure everything is running perfectly.',
  },
];

export default function ProcessSection() {
  return (
    <section id="process" data-testid="process-section" className="py-24 md:py-32 bg-[#F8F9FA] dark:bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400 block mb-4">
            How It Works
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-4 max-w-lg">
            From Idea to Launch in 4 Steps
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 dark:text-slate-400 mb-16 max-w-lg">
            A simple, clear process that keeps you in control at every stage.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {STEPS.map((step, i) => (
            <ScrollReveal key={step.title} delay={i * 120}>
              <div
                data-testid={`process-step-${i}`}
                className="group relative bg-white dark:bg-[#111111] border border-slate-200 dark:border-white/10 rounded-2xl p-8 glow-hover"
              >
                <span className="font-['Outfit'] text-5xl font-black text-slate-100 dark:text-white/5 absolute top-4 right-4 leading-none group-hover:text-[#00E5FF]/15 transition-colors">
                  {step.number}
                </span>
                <div className="w-10 h-10 rounded-lg bg-[#0A0A0A] dark:bg-[#8B5CF6]/10 flex items-center justify-center mb-5 group-hover:bg-[#00E5FF] transition-colors duration-300">
                  <step.icon className="w-4 h-4 text-white group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold tracking-tight text-[#0A0A0A] dark:text-white mb-2 relative">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 relative">
                  {step.description}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
